import React, { useState, Suspense, lazy } from 'react';
import { compose } from 'redux';
import { useIntl } from 'react-intl';
import { bool, object } from 'prop-types';
import { connect } from 'react-redux';
import { getListingsById } from '../../ducks/marketplaceData.duck';

import loadable from '@loadable/component';
import { Modal } from '../../components';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';

import { manageDisableScrolling } from '../../ducks/ui.duck';
import css from './ContactUsForm.module.css';
import { sendMail } from '../../util/api';

// Lazy-load PageBuilder and ContactUsForm — they are large and not needed for initial paint
const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);
const ContactUsForm = loadable(() =>
  import(/* webpackChunkName: "ContactUsForm" */ './ContactUsForm')
);

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error, onManageDisableScrolling } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const intl = props.intl || useIntl();

  const handleOpen = () => setIsModalOpen(true);

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const onSubmit = values => {
    const { email, company_name, systemType, systemScope, pallet_postion, location, additional_details } = values;
    const systemScopeLabel =
      systemScope === 'component' ? 'Component' :
      systemScope === 'complete-system' ? 'Complete System' :
      systemScope === 'not-sure' ? 'Not Sure' : '';
    // FieldLocationAutocompleteInput stores an object { search, predictions, selectedPlace }.
    // The email template expects a plain address string, so send only the search text.
    const locationText =
      typeof location === 'string' ? location : location?.search || '';
    const payload = {
      email,                                    // submitter's email (kept for the template / reply)
      // Company inbox + CC — configurable via env, with sensible defaults.
      toEmail: process.env.REACT_APP_CONTACT_US_RECIPIENT || 'info@palletrackmarket.com',
      cc: process.env.REACT_APP_CONTACT_US_CC || 'kp@palletrackmarket.com',
      company_name,
      systemType,                               // the actual "Type of System" dropdown value
      systemScope: systemScopeLabel,            // component / complete system / not sure
      pallet_postion,
      location: locationText,                   // plain address string for the template
      additional_details,
    };
    const CONTACT_US_TEMPLATE_ID = process.env.REACT_APP_CONTACT_US_TEMPLATE_ID;
    sendMail({
      isCallBackFn: false, template_id: CONTACT_US_TEMPLATE_ID, data: payload 
    }).then(response => { 
    }).catch(error => {
      console.error(error);
    });
    handleClose();
  };

  return (
    <>
      <PageBuilder
        pageAssetsData={pageAssetsData?.[camelize(ASSET_NAME)]?.data}
        inProgress={inProgress}
        error={error}
        fallbackPage={<FallbackPage error={error} />}
        handleOpen={handleOpen}
        handleClose={handleClose}
        featuredListings={props.featuredListings}
        featureOfTheWeek={props.featureOfTheWeek}
      />
      {/* contact-us modal */}
      <Modal
        className={css.contactusModal}
        id="contactUs"
        isOpen={isModalOpen}
        onClose={() => {
          handleClose()
        }}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        
      >
        <ContactUsForm
          onSubmit={onSubmit}
          intl={intl}
        />
      </Modal>
      {/* end */}
    </>
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};



const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const { currentPageFeaturedListingsResultIds, featuredListingId } = state.LandingPage || {};
  const featuredListings = currentPageFeaturedListingsResultIds
    ? getListingsById(state, currentPageFeaturedListingsResultIds)
    : [];
  
  const featureOfTheWeek = featuredListingId
    ? getListingsById(state, [featuredListingId])
    : [];

  return { pageAssetsData, inProgress, error, featuredListings, featureOfTheWeek };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling))
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(connect(mapStateToProps, mapDispatchToProps))(LandingPageComponent);

export default LandingPage;
